import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { slotsApi, logsApi, devicesApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import PageHeader from '../components/PageHeader';
import { StatGrid, StatTile } from '../components/ResponsiveTable';
import usePageTitle from '../hooks/usePageTitle';
import { ScrollText, AlertCircle, Info, AlertTriangle, Pause, Play, Smartphone, Layers, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

/*
 * Logs (Aurora, 2026-08-23). The console is a fixed-height, vertically scrolling monospace box
 * inside its own glass card: it never widens the page. On phones each line wraps to two rows
 * (icon + time + slot, then the message); from sm up it is one row with the message filling.
 */

function errorText(error) {
  return error?.response?.data?.message || error?.message || 'The server did not answer.';
}

/* skeleton "lines" while the first page of logs loads - varied widths so it reads as log text */
const SKELETON_WIDTHS = ['w-3/5', 'w-2/5', 'w-4/5', 'w-1/2', 'w-3/4', 'w-2/5', 'w-3/5', 'w-1/3'];

export default function LogsPage() {
  usePageTitle('Logs');
  const [selectedSlot, setSelectedSlot] = useState('all');
  const [selectedDevice, setSelectedDevice] = useState('all');
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const logsEndRef = useRef(null);

  const { data: slots } = useQuery({
    queryKey: ['slots'],
    queryFn: () => slotsApi.list().then(res => res.data),
  });

  const { data: devices } = useQuery({
    queryKey: ['devices'],
    queryFn: () => devicesApi.list().then(res => res.data),
  });

  const { data: logs, isLoading, isError, error, isFetching, refetch } = useQuery({
    queryKey: ['logs', selectedSlot, selectedDevice, errorsOnly],
    queryFn: () => logsApi.list({
      slot_id: selectedSlot !== 'all' ? selectedSlot : undefined,
      device_id: selectedDevice !== 'all' ? selectedDevice : undefined,
      errors_only: errorsOnly,
      limit: 200,
    }).then(res => res.data),
    refetchInterval: 5000, // Auto-refresh every 5 seconds
  });

  useEffect(() => {
    if (autoScroll && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, autoScroll]);

  const getLogLevelIcon = (isError) => {
    if (isError) return <AlertCircle className="h-4 w-4 text-danger" />;
    return <Info className="h-4 w-4 text-primary" />;
  };

  const getLogLevelClass = (isError) => {
    if (isError) return 'border-l-2 border-danger/50';
    return 'border-l-2 border-primary/30';
  };

  // Count helpers for stats
  const errorCount = logs?.filter(l => l.level === 'error' || l.is_error).length || 0;
  const infoCount = logs?.filter(l => l.level !== 'error' && !l.is_error).length || 0;
  const slotsWithErrors = new Set(logs?.filter(l => l.level === 'error' || l.is_error).map(l => l.slot_id_str || l.slot_name)).size || 0;

  const hasLogs = !!(logs && logs.length > 0);
  const filtersActive = selectedSlot !== 'all' || selectedDevice !== 'all' || errorsOnly;

  return (
    <div data-testid="logs-page">
      {/* Header */}
      <PageHeader icon={ScrollText} title="Logs" description="Real-time session logs and activity" />

      <div className="space-y-6">
        {/* Filters */}
        <Card className="aurora-card" data-testid="logs-filters">
          <CardContent className="p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
              {/* Slot Filter */}
              <div className="flex min-w-0 items-center gap-2">
                <Layers className="h-4 w-4 shrink-0 text-primary" />
                <Label className="shrink-0 text-xs font-medium uppercase tracking-wide text-muted-foreground">Slot:</Label>
                <Select value={selectedSlot} onValueChange={setSelectedSlot}>
                  <SelectTrigger
                    className="w-full min-w-0 border-border bg-secondary/60 text-foreground sm:w-[180px]"
                    aria-label="Filter by slot"
                    data-testid="logs-slot-filter"
                  >
                    <SelectValue placeholder="All Slots" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Slots</SelectItem>
                    {slots?.map((slot) => {
                      const slotId = slot.slot_id || `slot${slot.slot_number}`;
                      return (
                        <SelectItem
                          key={slot.id || slotId}
                          value={slotId}
                        >
                          {slot.slot_label || slot.slot_name} ({slotId})
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {/* Device Filter */}
              <div className="flex min-w-0 items-center gap-2">
                <Smartphone className="h-4 w-4 shrink-0 text-success" />
                <Label className="shrink-0 text-xs font-medium uppercase tracking-wide text-muted-foreground">Device:</Label>
                <Select value={selectedDevice} onValueChange={setSelectedDevice}>
                  <SelectTrigger
                    className="w-full min-w-0 border-border bg-secondary/60 text-foreground sm:w-[180px]"
                    aria-label="Filter by device"
                    data-testid="logs-device-filter"
                  >
                    <SelectValue placeholder="All Devices" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Devices</SelectItem>
                    {devices?.map((device) => {
                      const deviceId = device.device_id || `device${device.device_number}`;
                      return (
                        <SelectItem
                          key={device.id || deviceId}
                          value={deviceId}
                        >
                          {device.label || device.device_name} ({deviceId})
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {/* Errors Only Toggle */}
              <div className="flex items-center gap-2 sm:ml-auto">
                <Switch
                  id="errors-only"
                  checked={errorsOnly}
                  onCheckedChange={setErrorsOnly}
                />
                <Label htmlFor="errors-only" className="cursor-pointer text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Errors only
                </Label>
              </div>

              {/* Clear Filters */}
              {filtersActive && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedSlot('all');
                    setSelectedDevice('all');
                    setErrorsOnly(false);
                  }}
                  className="tap self-start text-muted-foreground hover:text-foreground sm:self-auto"
                  data-testid="logs-clear-filters"
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Log Console */}
        <Card className="aurora-card overflow-hidden" data-testid="logs-console">
          <CardHeader className="border-b border-border/70 px-4 py-3 sm:px-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="flex items-center gap-2 font-heading text-base font-semibold">
                <ScrollText className="h-5 w-5 text-primary" />
                Console Output
              </CardTitle>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setAutoScroll(!autoScroll)}
                  aria-pressed={autoScroll}
                  className={cn('tap', autoScroll ? 'text-primary' : 'text-muted-foreground')}
                  data-testid="logs-autoscroll-toggle"
                >
                  {autoScroll ? <Pause className="h-4 w-4 mr-1" /> : <Play className="h-4 w-4 mr-1" />}
                  {autoScroll ? 'Auto-scroll ON' : 'Auto-scroll OFF'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => refetch()}
                  className="tap text-muted-foreground hover:text-foreground"
                  title="Refresh now (auto-refreshes every 5 s)"
                  data-testid="logs-refresh"
                >
                  <RefreshCw className={cn('h-4 w-4 mr-1', isFetching && !isLoading && 'animate-spin text-primary')} />
                  Refresh
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {/* the console: fixed height, scrolls vertically, never sideways; monospace throughout */}
            <div
              className="h-[420px] overflow-y-auto overflow-x-hidden bg-background/70 p-3 font-mono text-xs sm:h-[500px] sm:p-4 sm:text-sm"
              data-testid="logs-console-scroll"
            >
              {isLoading ? (
                <div className="space-y-2" aria-busy="true" aria-label="Loading logs" data-testid="logs-skeleton">
                  {SKELETON_WIDTHS.map((w, i) => (
                    <div key={i} className="flex items-center gap-3 px-2 py-1">
                      <Skeleton className="h-4 w-4 shrink-0 rounded-full bg-secondary/60" />
                      <Skeleton className="h-4 w-14 shrink-0 rounded-md bg-secondary/60" />
                      <Skeleton className="hidden h-4 w-16 shrink-0 rounded-md bg-secondary/60 sm:block" />
                      <Skeleton className={cn('h-4 rounded-md bg-secondary/60', w)} />
                    </div>
                  ))}
                </div>
              ) : isError && !hasLogs ? (
                <div className="flex h-full flex-col items-center justify-center text-center font-sans" role="alert" data-testid="logs-error">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-danger/10 text-danger ring-1 ring-danger/20">
                    <AlertCircle className="h-5 w-5" />
                  </span>
                  <p className="mt-3 text-sm font-medium text-foreground">Could not load logs</p>
                  <p className="mt-0.5 max-w-xs text-xs text-muted-foreground text-pretty">{errorText(error)}</p>
                  <Button variant="outline" size="sm" onClick={() => refetch()} className="tap mt-4 rounded-full">
                    <RefreshCw className="h-4 w-4" /> Try again
                  </Button>
                </div>
              ) : hasLogs ? (
                <div className="space-y-1">
                  {isError && (
                    <div
                      className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 font-sans text-xs"
                      role="alert"
                      data-testid="logs-refresh-error"
                    >
                      <AlertCircle className="h-3.5 w-3.5 shrink-0 text-danger" />
                      <span className="min-w-0 flex-1">
                        <span className="font-medium">Last refresh failed.</span>{' '}
                        <span className="text-muted-foreground">{errorText(error)} Showing the last good lines.</span>
                      </span>
                    </div>
                  )}
                  {logs.slice().reverse().map((log, index) => (
                    <div
                      key={log.id || log._id || index}
                      className={cn(
                        'log-line flex flex-wrap items-start gap-x-3 gap-y-1 rounded px-2 py-1 hover:bg-secondary/60 sm:flex-nowrap',
                        getLogLevelClass(log.level === 'error' || log.is_error)
                      )}
                      data-testid={`log-item-${index}`}
                    >
                      <span className="flex-shrink-0">{getLogLevelIcon(log.level === 'error' || log.is_error)}</span>
                      <span className="flex-shrink-0 text-muted-foreground tnum">
                        {log.created_at ? format(new Date(log.created_at), 'HH:mm:ss') : '--:--:--'}
                      </span>
                      <Badge variant="outline" className="max-w-[10rem] min-w-0 flex-shrink-0 border-primary/30 text-xs text-primary">
                        <span className="truncate">{log.slot_id_str || log.slot_name || log.slots?.slot_name || 'system'}</span>
                      </Badge>
                      <span className="min-w-0 basis-full break-all text-foreground/90 sm:flex-1 sm:basis-auto">
                        {log.message || log.logs || 'No message'}
                      </span>
                    </div>
                  ))}
                  <div ref={logsEndRef} />
                </div>
              ) : (
                <div
                  className="flex h-full flex-col items-center justify-center rounded-xl border border-dashed border-border/70 bg-card/40 text-center font-sans text-muted-foreground"
                  data-testid="logs-empty"
                >
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/20">
                    <ScrollText className="h-5 w-5" />
                  </span>
                  <p className="mt-3 text-sm font-medium text-foreground">No logs to display</p>
                  <p className="mt-0.5 text-xs">Logs will appear here when automation runs</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <section aria-label="Log counts" data-testid="logs-stats">
          <StatGrid cols={4}>
            <StatTile className="aurora-card" icon={Info} tone="primary" label="Info Logs" value={infoCount} testId="logs-stat-info" />
            <StatTile className="aurora-card" icon={AlertCircle} tone="danger" label="Error Logs" value={errorCount} testId="logs-stat-errors" />
            <StatTile className="aurora-card" icon={ScrollText} tone="success" label="Total Logs" value={logs?.length || 0} testId="logs-stat-total" />
            <StatTile className="aurora-card" icon={AlertTriangle} tone="warning" label="Slots with Errors" value={slotsWithErrors} testId="logs-stat-slots-with-errors" />
          </StatGrid>
        </section>
      </div>
    </div>
  );
}
